// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TopNavMegaMenuItem.tsx
 * @input Uses React, StyleX, theme tokens, navItemStyles, TopNavRenderContext
 * @output Exports TopNavMegaMenuItem component and props
 * @position Individual item inside an TopNavMegaMenu — renders itself in both
 *   desktop (popover) and mobile drawer modes.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TopNav/TopNav.doc.mjs
 * - /packages/core/src/TopNav/index.ts
 * - /packages/cli/assets/templates/blocks/components/TopNav/ (showcase blocks)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, typeScaleVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { useTopNavRenderMode } from "./TopNavRenderContext.js";
import { mergeProps } from "../utils/index.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { themeProps } from "../utils/themeProps.js";
import { focusOutlineProps } from "../utils/focusOutline.stylex.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  desktop: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xjcht0a",
    k8WAf4: "x8o8v82",
    kg3NbH: "xrrkdod",
    kaIpWk: "xh6dtrn",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x15406qy",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kWkggS: "xjbqb8w xe9uy6x xyxi2l3",
    kMwMTN: "x1heor9g",
    kMv6JI: "xjb2p0i",
    k9WMMc: "x1yc453h",
    kB7OPa: "x9f619",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  drawerItem: {
    kZCmMZ: "x31w388",
    kGNEyG: "x1cy8zhl",
    kybGjl: "x1hl2dhg",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * An individual item inside an TopNavMegaMenu.
 *
 * Renders itself in both desktop (popover grid) and mobile drawer modes
 * using TopNavRenderContext to switch appearance.
 *
 * @example
 * ```
 * <TopNavMegaMenu
 *   label="Products"
 *   items={
 *     <>
 *       <TopNavMegaMenuItem
 *         title="Analytics"
 *         description="Track and analyze user behavior"
 *         icon={<ChartIcon />}
 *         href="/analytics"
 *       />
 *       <TopNavMegaMenuItem title="Reports" href="/reports" />
 *     </>
 *   }
 * />
 * ```
 */
export function TopNavMegaMenuItem({
  ref,
  title,
  description,
  icon,
  href,
  onClick,
  as,
  tabIndex
}) {
  const renderMode = useTopNavRenderMode();
  const LinkComponent = useLinkComponent(as);
  const {
    closeMobileNav
  } = useAppShellMobile();

  // =========================================================================
  // Drawer mode — matches SideNavItem / TopNavMenu drawer appearance
  // =========================================================================
  if (renderMode === 'drawer') {
    const Element = href ? LinkComponent : 'button';
    const elementProps = Element === 'button' ? {
      type: 'button'
    } : {};
    const handleDrawerClick = () => {
      onClick?.();
      closeMobileNav();
    };
    return /*#__PURE__*/_jsxs(Element, {
      ref: ref,
      href: href,
      onClick: handleDrawerClick,
      ...elementProps,
      ...mergeProps(themeProps('top-nav-mega-menu-item', {
        mode: 'drawer'
      }), focusOutlineProps.focusVisible(navItemStyles.item, styles.drawerItem)),
      children: [icon && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x78zum5 x6s0dn4 xl56j7k x1td3qas x10w6t97 xh6dtrn x17x4s8c x2lah0s xv9yike xjc2qm6"
        },
        children: icon
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf x1lsbc85 xeuugli"
        },
        children: [title, description && /*#__PURE__*/_jsx("span", {
          ...{
            className: "x141an7d x1ltkj2j xv1l7n4 x1sodnla"
          },
          children: description
        })]
      })]
    });
  }

  // =========================================================================
  // Default mode — desktop popover item with large icon + description
  // =========================================================================
  const Element = href ? LinkComponent : 'div';
  return /*#__PURE__*/_jsxs(Element, {
    ref: ref,
    href: href,
    onClick: onClick,
    tabIndex: tabIndex,
    ...mergeProps(themeProps('top-nav-mega-menu-item'), focusOutlineProps.focusVisible(styles.desktop)),
    children: [icon && /*#__PURE__*/_jsx("div", {
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k x100vrsf x1vqgdyp xh6dtrn x17x4s8c x2lah0s xv9yike"
      },
      children: icon
    }), /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 xdt5ytf xzye2dw xeuugli"
      },
      children: [/*#__PURE__*/_jsx("span", {
        ...{
          className: "xcr08ib x1kq96og x2mo6ok x1tgivj0"
        },
        children: title
      }), description && /*#__PURE__*/_jsx("span", {
        ...{
          className: "x141an7d x1ltkj2j x1sodnla xv1l7n4"
        },
        children: description
      })]
    })]
  });
}
TopNavMegaMenuItem.displayName = 'TopNavMegaMenuItem';